import React, { useContext, useEffect, useState, useMemo, FC } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Flex, Typography, Avatar, Tabs, Spin, Tooltip } from 'antd'
import { EnvironmentOutlined, CalendarOutlined } from '@ant-design/icons'
import moment from 'moment'
import { VerticalContext } from 'store/verticals/Context'
import { selectCurrentZone } from 'store/global'
import type { CurrentZone } from 'utils/types'
import { NetworkGraphButton } from 'components/network-graph/NetworkGraphButton'
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs'
import { changeCurrentZoneIndexesFunc } from 'containers/VerticalPage/common/utils'
import { NOT_AVAILABLE } from 'utils/constants'
import Overview from '../Overview/Overview'
import AggregateFlowsChart from '../Overview/AggregateFlowsChart'
import RevenueSourceCards from '../RevenueSourceCards/RevenueSourceCards'
import Activity from '../Activity/Activity'
import ContactInfo from 'containers/VerticalPage/common/ContactInfo/ContactInfo'
import { getCryptoSocialIcon } from '../assets/social-icons/socialIconMap'
import { getVerticalIcon } from './verticalIconMap'

const { Text, Title } = Typography

const VALUE_STYLE: React.CSSProperties = {
  fontWeight: 400,
  fontSize: 13,
  lineHeight: 1,
}

const ICON_STYLE: React.CSSProperties = { width: 14, height: 14, display: 'block' }
const VERTICAL_ICON_STYLE: React.CSSProperties = { ...ICON_STYLE, filter: 'brightness(0)' }

const TAB_ITEMS = [
  { key: 'overview', label: 'Overview' },
  { key: 'activity', label: 'Activity' },
  { key: 'contactinfo', label: 'Contact Info' },
]

const VerticalPage: FC<{ changeCurrentZoneIndexes: changeCurrentZoneIndexesFunc }> = ({ changeCurrentZoneIndexes }) => {
  const { entityId } = useParams<{ entityId: string }>()
  const { getVerticalEntity, verticalEntity } = useContext(VerticalContext)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentZone: CurrentZone = useSelector(selectCurrentZone as any)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    changeCurrentZoneIndexes({
      baseIndices: currentZone?.cryptoIndices,
      activityIndices: currentZone?.cryptoActivityIndices,
    })
  }, [currentZone]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (entityId && currentZone?.cryptoIndices) {
      getVerticalEntity(entityId, currentZone.cryptoIndices)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, currentZone?.cryptoIndices])

  const entity = verticalEntity

  const socialIcons = useMemo(() => {
    const urls = entity?.entityDetails?.profileUrls ?? []
    return urls
      .map((url: string) => {
        const iconSrc = getCryptoSocialIcon(url)
        if (!iconSrc) return null
        return (
          <a key={url} href={url} target='_blank' rel='noreferrer' style={{ display: 'inline-flex' }}>
            <img src={iconSrc} alt='social' style={ICON_STYLE} />
          </a>
        )
      })
      .filter(Boolean)
  }, [entity?.entityDetails?.profileUrls])

  const verticalIcons = useMemo(() => {
    const verticals = entity?.overviewDetails?.activeVerticals ?? []
    return verticals
      .map((v: string) => {
        const icon = getVerticalIcon(v)
        if (!icon) return null
        return (
          <Tooltip key={v} title={v}>
            <img src={icon} alt={v} style={VERTICAL_ICON_STYLE} />
          </Tooltip>
        )
      })
      .filter(Boolean)
  }, [entity?.overviewDetails?.activeVerticals])

  if (!entity) {
    return (
      <Flex justify='center' align='center' style={{ height: '60vh' }}>
        <Spin size='large' />
      </Flex>
    )
  }

  const { entityDetails } = entity

  const tabContent: Record<string, React.ReactNode> = {
    overview: (
      <>
        <Overview />
        <AggregateFlowsChart />
        <RevenueSourceCards revenueSources={entity.revenueSources} />
      </>
    ),
    activity: <Activity />,
    contactinfo: <ContactInfo />,
  }

  return (
    <div style={{ padding: '16px 24px' }}>
      <Breadcrumbs data={[{ label: 'Crypto', href: '/crypto' }, { label: entityDetails.name || NOT_AVAILABLE }]} />
      <Flex
        align='center'
        justify='space-between'
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: '12px 20px',
          marginBottom: 16,
          border: '1px solid #f0f0f0',
        }}
      >
        <Flex align='center' gap={16}>
          <Avatar src={entityDetails.image} size={48}>
            {entityDetails.name?.[0]}
          </Avatar>
          <div>
            <Flex align='center' gap={8}>
              <Title level={5} style={{ margin: 0 }}>
                {entityDetails.name}
              </Title>
              {entityDetails.tin && (
                <Text type='secondary' style={{ fontSize: 13 }}>
                  | TIN: {entityDetails.tin}
                </Text>
              )}
            </Flex>
            <Flex align='center' gap={16} style={{ marginTop: 4 }}>
              {entityDetails.birthday && (
                <Flex align='center' gap={4}>
                  <CalendarOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                  <Text style={VALUE_STYLE}>{moment(entityDetails.birthday).format('MMM Do, YYYY')}</Text>
                </Flex>
              )}
              {entityDetails.location && (
                <Flex align='center' gap={4}>
                  <EnvironmentOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                  <Text style={VALUE_STYLE}>{entityDetails.location}</Text>
                </Flex>
              )}
              {verticalIcons.length > 0 && (
                <Flex align='center' gap={6}>
                  <Text type='secondary' style={{ fontSize: 13, lineHeight: 1 }}>
                    Verticals:
                  </Text>
                  <Flex align='center' gap={4}>
                    {verticalIcons}
                  </Flex>
                </Flex>
              )}
              {socialIcons.length > 0 && (
                <Flex align='center' gap={6}>
                  <Text type='secondary' style={{ fontSize: 13, lineHeight: 1 }}>
                    Social:
                  </Text>
                  <Flex align='center' gap={4}>
                    {socialIcons}
                  </Flex>
                </Flex>
              )}
            </Flex>
          </div>
        </Flex>

        <NetworkGraphButton />
      </Flex>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={TAB_ITEMS} />
      {tabContent[activeTab]}
    </div>
  )
}

export default VerticalPage
